// src/lib/friends.js
// Freunde-Funktion: Einladungscodes, Freundesliste, eigene Kennzahlen für Freunde.
// Tabellen und Funktionen in scripts/create-friends.sql (vom Nutzer im SQL-Editor anzulegen).
// Andere Nutzer sind per RLS gesperrt; Freunde sieht man nur über die RPCs.

import { supabase } from "../supabaseClient";
import { getSessionUser } from "./sessionUser";
import { isMissingInSupabase } from "./activityLog";

// Einladungslink: ?friend=CODE öffnet die App mit vorausgefülltem Code
export const FRIEND_PARAM = "friend";
const PENDING_KEY = "fluent_pending_friend_code";

/** Code auf 8 erlaubte Zeichen bringen (Leerzeichen, Bindestriche, Kleinschreibung egal) */
export function normalizeFriendCode(input) {
  return String(input || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
}

/** Anzeige als „ABCD-EFGH“ */
export function formatFriendCode(code) {
  const c = normalizeFriendCode(code);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

export function friendInviteUrl(code) {
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?${FRIEND_PARAM}=${normalizeFriendCode(code)}`;
}

/**
 * Code aus dem Einladungslink übernehmen: aus der Adresszeile entfernen und bis nach
 * dem Login merken (sessionStorage). Beim Laden der App einmal aufrufen.
 */
export function captureFriendCodeFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const code = normalizeFriendCode(url.searchParams.get(FRIEND_PARAM));
    if (!url.searchParams.has(FRIEND_PARAM)) return;
    url.searchParams.delete(FRIEND_PARAM);
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    if (code.length === 8) window.sessionStorage.setItem(PENDING_KEY, code);
  } catch (e) {
    console.warn("captureFriendCodeFromUrl failed:", e);
  }
}

/** Gemerkten Code aus dem Einladungslink abholen (danach vergessen) */
export function takePendingFriendCode() {
  try {
    const code = window.sessionStorage.getItem(PENDING_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
    return code || null;
  } catch {
    return null;
  }
}

/** Neuen Einladungscode erzeugen → { code, expiresAt } */
export async function createFriendCode() {
  const { data, error } = await supabase.rpc("create_friend_code");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return { code: row.code, expiresAt: row.expires_at };
}

/**
 * Code einlösen → { status, friendId, displayName }
 * status: "ok" | "already" | "self" | "invalid"
 */
export async function redeemFriendCode(code) {
  const { data, error } = await supabase.rpc("redeem_friend_code", {
    p_code: normalizeFriendCode(code),
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) || {};
  return {
    status: row.status || "invalid",
    friendId: row.friend_id || null,
    displayName: row.display_name || null,
  };
}

export async function removeFriend(friendId) {
  const { error } = await supabase.rpc("remove_friend", { p_friend: friendId });
  if (error) throw error;
}

/** Freundesliste inkl. eigener Zeile (isMe) mit Kennzahlen */
export async function loadFriends() {
  const { data, error } = await supabase.rpc("get_friends");
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.friend_id,
    isMe: r.is_me,
    name: r.display_name,
    friendsSince: r.friends_since,
    languageId: r.language_id,
    pace: r.pace,
    currentWeek: r.current_week,
    weekExp: r.week_exp,
    weekExpMax: r.week_exp_max,
    masteredWords: r.mastered_words,
    statsUpdatedAt: r.stats_updated_at,
    minutes7d: r.minutes_7d || 0,
    streakDays: r.streak_days || 0,
    lastActive: r.last_active,
    friendExp: r.friend_exp || 0, // ab Phase 2 (create-friend-attacks.sql)
    totalExp: r.total_exp ?? null, // add-friend-total-exp.sql
    defenseStreak: r.defense_streak || 0,
    shieldUntil: r.shield_until || null,
  }));
}

// ---------- Phase 2: Angriffe, Blitzabfrage, Freunde-EXP ----------
// SQL in scripts/create-friend-attacks.sql

/** Titel für eine Abwehr-Serie (Abwehren in Folge) */
export function defenseTitle(streak) {
  if (streak >= 10) return "Festung";
  if (streak >= 3) return "Wachposten";
  return null;
}

/**
 * Freund angreifen → { status, attackId, expiresAt, counter }
 * status: "ok" | "not_friend" | "pending" | "shielded" | "busy" | "daily_limit"
 */
export async function attackFriend(friendId) {
  const { data, error } = await supabase.rpc("attack_friend", { p_friend: friendId });
  if (error) throw error;
  return {
    status: data?.status || "error",
    attackId: data?.attack_id || null,
    expiresAt: data?.expires_at || null,
    counter: !!data?.counter,
    shieldUntil: data?.shield_until || null,
  };
}

/**
 * Überblick: eigene Freunde-EXP, offene Angriffe auf mich, letzte Duelle (7 Tage)
 * und ob ich die einzelnen Freunde gerade angreifen darf (targets[friendId].status).
 */
export async function loadFriendDuels() {
  const { data, error } = await supabase.rpc("get_friend_duels");
  if (error) throw error;
  const me = data?.me || {};
  return {
    me: {
      exp: me.exp || 0,
      defensesWon: me.defenses_won || 0,
      defensesLost: me.defenses_lost || 0,
      defenseStreak: me.defense_streak || 0,
      bestStreak: me.best_streak || 0,
      hits: me.hits || 0,
      shieldUntil: me.shield_until || null,
    },
    incoming: (data?.incoming || []).map((a) => ({
      id: a.id,
      attackerId: a.attacker_id,
      attackerName: a.attacker_name,
      createdAt: a.created_at,
      expiresAt: a.expires_at,
      started: !!a.started,
      isCounter: !!a.is_counter,
    })),
    recent: (data?.recent || []).map((d) => ({
      id: d.id,
      role: d.role, // "attacker" | "defender"
      otherName: d.other_name,
      status: d.status, // "defended" | "failed" | "expired"
      score: d.score,
      total: d.total,
      finishedAt: d.finished_at,
    })),
    targets: data?.targets || {},
  };
}

/** Blitzabfrage beginnen. status: "ok" | "started" | "expired" | "not_found" */
export async function startDefense(attackId) {
  const { data, error } = await supabase.rpc("start_defense", { p_attack: attackId });
  if (error) throw error;
  return data?.status || "error";
}

/**
 * Blitzabfrage abschließen → { status, passed, perfect, gain, defenseStreak, bestStreak, shieldUntil }
 */
export async function finishDefense(attackId, score, total) {
  const { data, error } = await supabase.rpc("finish_defense", {
    p_attack: attackId,
    p_score: score,
    p_total: total,
  });
  if (error) throw error;
  return {
    status: data?.status || "error",
    passed: !!data?.passed,
    perfect: !!data?.perfect,
    gain: data?.gain || 0,
    defenseStreak: data?.defense_streak || 0,
    bestStreak: data?.best_streak || 0,
    shieldUntil: data?.shield_until || null,
  };
}

// Eigene Kennzahlen nur schicken, wenn sie sich geändert haben.
// Fehlt die Tabelle (SQL noch nicht ausgeführt), für diese Sitzung abschalten.
let lastPublished = null;
let publishDisabled = false;
// Fehlt nur die Spalte total_exp (add-friend-total-exp.sql noch nicht ausgeführt), ohne sie weiter
let totalExpMissing = false;

/**
 * Eigene Kennzahlen für Freunde ablegen (Tabelle friend_stats).
 * @param {{languageId, pace, currentWeek, weekExp, weekExpMax, masteredWords, totalExp}} stats
 */
export async function publishFriendStats(stats) {
  if (publishDisabled) return;
  const key = JSON.stringify(stats);
  if (key === lastPublished) return;
  lastPublished = key;

  try {
    const user = (await getSessionUser()).data.user;
    if (!user) return;
    const row = {
      user_id: user.id,
      language_id: stats.languageId,
      pace: stats.pace,
      current_week: stats.currentWeek,
      week_exp: stats.weekExp,
      week_exp_max: stats.weekExpMax,
      mastered_words: stats.masteredWords,
      updated_at: new Date().toISOString(),
    };
    // Gesamt-EXP nur mitschicken, wenn bekannt (sonst bliebe der alte Wert stehen)
    if (stats.totalExp != null && !totalExpMissing) row.total_exp = stats.totalExp;
    const upsert = (r) => supabase.from("friend_stats").upsert(r, { onConflict: "user_id" });

    let { error } = await upsert(row);
    if (error && "total_exp" in row && String(error.message || "").includes("total_exp")) {
      totalExpMissing = true;
      delete row.total_exp;
      ({ error } = await upsert(row));
    }
    if (isMissingInSupabase(error)) {
      publishDisabled = true;
      console.warn("publishFriendStats disabled:", error.message || error);
    } else if (error) {
      // beim nächsten Aufruf erneut versuchen
      lastPublished = null;
      console.warn("publishFriendStats failed:", error.message || error);
    }
  } catch (e) {
    lastPublished = null;
    console.warn("publishFriendStats failed:", e);
  }
}
